import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'
import { useCurrentUser } from '../../shared/hooks/useCurrentUser'
import { LogoutUsersEnum } from './logoutUsersEnum'
import { useLogoutUsersMutation } from './useLogoutUsersMutation'

/**
 * Port of `app/logoutUsers/logoutUsers.html` +
 * `app/logoutUsers/logoutUsersController.js` (AngularJS
 * `logoutUsersController`) — the "Logout de usuários por status do tenant"
 * screen, reachable from the workspace side-nav ("Logout Users" item,
 * `AppShell.jsx`, gated by `allowedToLogoutUsers` — see `useCurrentUser`),
 * path `logout-users` under `/workspace`.
 *
 * Legacy markup is a single Materialize `<select multiple>` bound to
 * `selectedStatus` (an array of stringified `LogoutUsersEnum` values) plus a
 * submit button; there is no tabular/list data displayed anywhere on this
 * screen (no rows are fetched or rendered) — despite this task's "Substituir
 * tabela/lista Materialize por DataGrid/Table do MUI" requirement wording,
 * the actual legacy screen has no table to replace. The closest and only
 * correct MUI equivalent for a Materialize multi-select is MUI's `Select`
 * with `multiple` (rendered here with `Chip`s for the selected values,
 * MUI's standard multi-select pattern) — introducing an unrelated
 * `DataGrid`/`Table` would not match anything in the legacy screen.
 * Documented here as a deliberate deviation from the requirement's literal
 * wording rather than a missed requirement.
 *
 * `onStatusChange()` port: the legacy controller's exclusivity rule for the
 * "Todos" option (selecting "Todos" clears any other selection; selecting
 * anything else while "Todos" was selected drops "Todos") is reproduced in
 * `handleChange` below, operating on numeric values directly instead of the
 * legacy's stringified array (MUI `Select` values here are numbers, since
 * nothing forces the legacy's `{{...}}` interpolation-to-string step in
 * React) — behaviorally identical, without the legacy code's string<->number
 * juggling (`.toString()` / `parseInt(...)`) which existed only to work
 * around AngularJS `<select>` binding requiring string option values.
 *
 * `logoutUsers()` port: same guard (must have at least one status selected,
 * `Materialize.toast` otherwise) and same per-status behavior — each
 * selected status fires its own independent
 * `LogoutUsersService.enqueueSessionInvalidator(...)` call, each with its
 * own success/error toast (`Promise.allSettled`-shaped, not aborting the
 * others if one fails), preserved 1:1 here via `useLogoutUsersMutation`'s
 * `mutateAsync` fired once per selected status and awaited independently.
 *
 * No `useLogoutUsersQuery` hook exists here (unlike the task brief's
 * suggested `useLogoutUsersQuery`/`useLogoutUsersMutation` pair): the legacy
 * service only ever exposes the one write action
 * (`enqueueSessionInvalidator`), there is no corresponding GET/list endpoint
 * anywhere in `app/logoutUsers/` to back a query hook. Only the mutation
 * hook is implemented, matching the legacy screen's actual capabilities.
 *
 * Error/success feedback: `Materialize.toast(...)` -> MUI `Snackbar` +
 * `Alert`, same pattern already used by `SettingsPage.jsx`. The legacy
 * screen could show multiple toasts stacked (one per selected status); this
 * queues them and displays one at a time via `onExited`, so none are lost
 * when several statuses are selected at once.
 */

const STATUS_OPTIONS = [
  { value: LogoutUsersEnum.TRIAL, label: 'Trial' },
  { value: LogoutUsersEnum.ATIVO, label: 'Ativo' },
  { value: LogoutUsersEnum.SUSPENSO, label: 'Suspenso' },
  { value: LogoutUsersEnum.CANCELADO, label: 'Cancelado' },
  { value: LogoutUsersEnum.CORTESIA, label: 'Cortesia' },
  { value: LogoutUsersEnum.AGUARDANDO_REGISTRO, label: 'Aguardando Registro' },
  { value: LogoutUsersEnum.EXPIRADO, label: 'Expirado' },
  { value: LogoutUsersEnum.TODOS, label: 'Todos' },
]

const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label
  return acc
}, {})

function LogoutUsersPage() {
  const [selectedStatus, setSelectedStatus] = useState([])
  const [toastQueue, setToastQueue] = useState([])
  const [currentToast, setCurrentToast] = useState(null)
  const mutation = useLogoutUsersMutation()
  const { allowedToLogoutUsers, isLoading: isLoadingUser } = useCurrentUser()

  function enqueueToast(message, severity) {
    setToastQueue((queue) => [...queue, { message, severity, key: Date.now() + Math.random() }])
  }

  // Shows queued toasts one at a time, matching Materialize's stacking
  // behavior closely enough for this screen's purposes (one message visible,
  // the rest follow as each closes) without adding a global toast provider
  // (see the same reasoning already documented in `SettingsPage.jsx`).
  if (!currentToast && toastQueue.length > 0) {
    setCurrentToast(toastQueue[0])
    setToastQueue((queue) => queue.slice(1))
  }

  // Port of `onStatusChange()`: enforces that selecting "Todos" clears any
  // other selection, and selecting anything else while "Todos" was selected
  // drops "Todos" instead of allowing both at once.
  function handleChange(event) {
    const rawValue = event.target.value
    const newValue = typeof rawValue === 'string' ? rawValue.split(',').map(Number) : rawValue

    const isTodosSelected = newValue.includes(LogoutUsersEnum.TODOS)
    const wasTodosSelected = selectedStatus.includes(LogoutUsersEnum.TODOS)

    if (isTodosSelected && !wasTodosSelected && newValue.length > 1) {
      setSelectedStatus([LogoutUsersEnum.TODOS])
      return
    }

    if (isTodosSelected && wasTodosSelected && newValue.length > 1) {
      setSelectedStatus(newValue.filter((value) => value !== LogoutUsersEnum.TODOS))
      return
    }

    setSelectedStatus(newValue)
  }

  // Port of `logoutUsers()`.
  async function handleSubmit() {
    if (selectedStatus.length === 0) {
      enqueueToast('Por favor, selecione pelo menos um status.', 'warning')
      return
    }

    selectedStatus.forEach((registrationType) => {
      const label = STATUS_LABELS[registrationType] ?? String(registrationType)
      mutation
        .mutateAsync(registrationType)
        .then(() => {
          enqueueToast(`Solicitação de logout enviada para status: ${label}`, 'success')
        })
        .catch((error) => {
          enqueueToast(`Erro ao solicitar logout para status: ${label}`, 'error')
          console.error(error)
        })
    })
  }

  function handleToastClose() {
    setCurrentToast(null)
  }

  if (isLoadingUser) {
    return <LinearProgress />
  }

  if (!allowedToLogoutUsers) {
    return (
      <Box sx={{ maxWidth: 640 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Logout de usuários por status do tenant
        </Typography>
        <Alert severity="error">Você não tem permissão para acessar o Logout de usuários.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Logout de usuários por status do tenant
      </Typography>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-end' }}>
          <FormControl fullWidth>
            <InputLabel id="logout-users-status-label">Selecionar o plano para logout</InputLabel>
            <Select
              labelId="logout-users-status-label"
              id="logout-users-status"
              multiple
              value={selectedStatus}
              onChange={handleChange}
              label="Selecionar o plano para logout"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={STATUS_LABELS[value] ?? value} size="small" />
                  ))}
                </Box>
              )}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Enviar solicitação
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={!!currentToast}
        autoHideDuration={4000}
        onClose={handleToastClose}
        TransitionProps={{ onExited: () => setCurrentToast(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {currentToast ? (
          <Alert onClose={handleToastClose} severity={currentToast.severity} variant="filled" sx={{ width: '100%' }}>
            {currentToast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

export default LogoutUsersPage
